/**
 * GetEquity Exchange — derived helpers.
 *
 * Pure functions that turn a raw listing (TokenResponseModel) or market
 * activity item into the values the Exchange UI renders. Kept framework-free
 * so both the page and its child components can share one source of truth.
 *
 * See the Exchange build spec §10–§11 for the exact rules encoded here.
 */

const FIXED_INCOME_TYPES = ["debt", "fixed interest", "fund"];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const num = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/* ------------------------------------------------------------------ *
 * Currency
 * ------------------------------------------------------------------ */

export const CURRENCY_NAMES = {
  NGN: "Nigerian Naira",
  USD: "US Dollar",
  KES: "Kenyan Shilling",
  GBP: "British Pound",
  EUR: "Euro",
};

export const currencySymbol = (currency) => {
  switch ((currency || "").toUpperCase()) {
    case "NGN":
      return "₦"; // ₦
    case "USD":
      return "$";
    case "KES":
      return "KSh";
    case "GBP":
      return "£"; // £
    case "EUR":
      return "€"; // €
    default:
      return "$";
  }
};

export const currencyName = (currency) =>
  CURRENCY_NAMES[(currency || "").toUpperCase()] || (currency || "USD");

export const tokenCurrency = (token) => token.currency || "USD";

/**
 * Pick the currency the page should be scoped to.
 * User selection if it still has listings, else NGN, else the currency with
 * the most listings, else USD.
 */
export const resolveActiveCurrency = (listings, preferred) => {
  const counts = {};
  (listings || []).forEach((t) => {
    const c = tokenCurrency(t).toUpperCase();
    counts[c] = (counts[c] || 0) + 1;
  });
  const available = Object.keys(counts);
  if (!available.length) return preferred || "USD";
  if (preferred && counts[preferred.toUpperCase()]) return preferred.toUpperCase();
  if (counts.NGN) return "NGN";
  return available.reduce((a, b) => (counts[b] > counts[a] ? b : a));
};

export const distinctCurrencies = (listings) => {
  const set = new Set(
    (listings || []).map((t) => tokenCurrency(t).toUpperCase())
  );
  return Array.from(set);
};

/* ------------------------------------------------------------------ *
 * Per-listing derived values (spec §11)
 * ------------------------------------------------------------------ */

export const isFixedIncome = (token) =>
  FIXED_INCOME_TYPES.includes((token.investment_type || "").toLowerCase());

/** A commodity has a price and price movement only: no yield, valuation or maturity. */
export const isCommodity = (token) =>
  (token.investment_type || "").toLowerCase() === "commodity";

/** Class for the investment-type badge pill, matching the Invest page scheme. */
export const investmentTypeClass = (token) =>
  (token.investment_type || "equity").toLowerCase().replace(/\s+/g, "-");

export const listingYield = (token) => num(token.interest);

export const listingPrice = (token) => {
  const exchange = token.price && token.price.exchange;
  const buy = token.price && token.price.buy;
  return num(exchange != null ? exchange : buy);
};

export const totalMarketValue = (token) =>
  num(token.total_supply) * listingPrice(token);

export const minPurchase = (token) => {
  const trade = token.min_trade && token.min_trade.buy;
  const min = token.min && token.min.buy;
  return num(trade != null ? trade : min);
};

export const daysRemaining = (token) => {
  // A commodity has no maturity; any date on the record is a placeholder.
  if ((token.investment_type || "").toLowerCase() === "commodity") return 0;
  if (token.maturity) {
    const now = Date.now();
    const maturity = new Date(token.maturity).getTime();
    if (Number.isFinite(maturity)) {
      const days = Math.ceil((maturity - now) / (1000 * 60 * 60 * 24));
      return Math.max(days, 0);
    }
  }
  return Math.max(num(token.tenor), 0);
};

export const soldRatio = (token) => {
  const total = num(token.total_supply);
  if (total <= 0) return 0;
  return clamp((total - num(token.supply)) / total, 0, 1);
};

export const remainingRatio = (token) => {
  const total = num(token.total_supply);
  if (total <= 0) return 0;
  return clamp(num(token.supply) / total, 0, 1);
};

export const changePercentage = (token) => num(token.percentage);

export const sellOrderTotal = (token) => num(token.sellOrderTotal);

/** What the active sell orders are worth: what can be bought right now, as the mobile app shows it. */
export const sellOrderValue = (token) => num(token.sellOrderValue);

/** Discovery score used to rank Top Opportunities (spec §8). */
export const opportunityScore = (token) => {
  const hasRemaining = num(token.supply) > 0;
  return listingYield(token) + (hasRemaining ? 5 : 0) + soldRatio(token) * 10;
};

/** Listed date — completed_raise_date, falling back to createdAt. */
export const listedDate = (token) => {
  const raw = token.completed_raise_date || token.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isFinite(d.getTime()) ? d : null;
};

export const daysSinceListed = (token) => {
  const listed = listedDate(token);
  if (!listed) return Infinity;
  return Math.floor((Date.now() - listed.getTime()) / (1000 * 60 * 60 * 24));
};

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

/** Compact number: ≥1e9→B, ≥1e6→M, ≥1e3→K, else integer. */
export const compact = (value) => {
  const n = num(value);
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return `${Math.round(n)}`;
};

export const compactMoney = (value, currency) =>
  `${currencySymbol(currency)}${compact(value)}`;

export const money = (value, currency, decimals = 2) => {
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num(value));
  return `${currencySymbol(currency)}${formatted}`;
};

/** Human relative time, e.g. "3d ago", "just now". */
export const relativeTime = (date) => {
  if (!date) return "";
  const then = new Date(date).getTime();
  if (!Number.isFinite(then)) return "";
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

/* ------------------------------------------------------------------ *
 * Market metrics (spec §11)
 * ------------------------------------------------------------------ */

export const exchangeVolume = (listings) =>
  (listings || []).reduce((sum, t) => sum + totalMarketValue(t), 0);

export const averageYield = (listings) => {
  const yields = (listings || [])
    .filter((t) => isFixedIncome(t) && listingYield(t) > 0)
    .map(listingYield);
  if (!yields.length) return 0;
  return yields.reduce((a, b) => a + b, 0) / yields.length;
};

export const tradesThisWeek = (activity) => {
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return (activity || []).filter((a) => {
    const t = new Date(a.createdAt).getTime();
    return Number.isFinite(t) && t >= weekAgo;
  }).length;
};

/* ------------------------------------------------------------------ *
 * Badges (spec §10)
 * ------------------------------------------------------------------ */

export const BADGE_COLORS = {
  new: "#4D84FF",
  highYield: "#F2994A",
  maturing: "#E84343",
  sellingFast: "#4D84FF",
  popular: "#4D84FF",
};

/**
 * Ordered list of badges that apply to a token. Cards show up to 1, tiles up
 * to 3. Priority order matches the spec, with a Popular fallback.
 */
export const listingBadges = (token) => {
  const badges = [];
  const fixed = isFixedIncome(token);
  const days = daysRemaining(token);

  if (daysSinceListed(token) <= 7) {
    badges.push({ key: "new", label: "New", color: BADGE_COLORS.new });
  }
  if (fixed && listingYield(token) >= 18) {
    badges.push({
      key: "highYield",
      label: "High Yield",
      color: BADGE_COLORS.highYield,
    });
  }
  if (fixed && days >= 1 && days <= 14) {
    badges.push({
      key: "maturing",
      label: "Maturing Soon",
      color: BADGE_COLORS.maturing,
    });
  }
  if (remainingRatio(token) <= 0.2 && num(token.supply) > 0) {
    badges.push({
      key: "sellingFast",
      label: "Selling Fast",
      color: BADGE_COLORS.sellingFast,
    });
  }
  if (!badges.length && soldRatio(token) >= 0.5) {
    badges.push({ key: "popular", label: "Popular", color: BADGE_COLORS.popular });
  }
  return badges;
};

/* ------------------------------------------------------------------ *
 * Filtering & sorting (spec §6–§8)
 * ------------------------------------------------------------------ */

export const distinctInvestmentTypes = (listings) => {
  const set = new Set(
    (listings || [])
      .map((t) => t.investment_type)
      .filter((t) => t && t.trim().length)
  );
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

export const searchListings = (listings, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return listings || [];
  return (listings || []).filter((t) => {
    const haystack = [
      t.name,
      t.symbol,
      t.investment_type,
      t.creator && t.creator.name,
      t.sharia_compliant === true && "sharia shariah compliant halal islamic",
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
};

export const sortByYieldDesc = (listings) =>
  [...(listings || [])].sort((a, b) => listingYield(b) - listingYield(a));

export const sortByListedDesc = (listings) =>
  [...(listings || [])].sort((a, b) => {
    const da = listedDate(a);
    const db = listedDate(b);
    return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
  });

export const maturingSoon = (listings) =>
  (listings || [])
    .filter((t) => {
      const d = daysRemaining(t);
      return d >= 1 && d <= 30;
    })
    .sort((a, b) => daysRemaining(a) - daysRemaining(b));
